import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Center menu: Community (dropdown), Explore (dropdown), Islam.
 * Auth (Login/Sign Up) is rendered by Header on the right.
 */
const Navigation = ({ onNavigate }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [exploreOpen, setExploreOpen] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(false);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/explore') return location.pathname === '/explore';
    if (path === '/community') return location.pathname === '/community';
    if (path === '/contact') return location.pathname === '/contact';
    if (path === '/scrapper') return location.pathname === '/scrapper';
    return location.pathname.startsWith(path);
  };

  const getLinkClasses = (path) => {
    const base = 'px-2 py-2 text-sm font-medium transition-colors relative rounded-xl';
    return isActive(path)
      ? `${base} text-primary-600 dark:text-primary-400 font-semibold`
      : `${base} text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white`;
  };

  const exploreItems = [
    { path: '/businesses', label: 'Businesses' },
    { path: '/events', label: 'Events' },
    { path: '/ads', label: 'Ads' },
    { path: '/properties', label: 'Properties' },
    { path: '/turfs', label: 'Turfs' },
    { path: '/points', label: 'Points' },
  ];

  const communityItems = [
    { path: '/societies', label: 'Societies' },
    { path: '/posts', label: 'Posts' },
  ];

  const isExploreActive = location.pathname === '/explore' || exploreItems.some((i) => i.path !== '/explore' && isActive(i.path));
  const isCommunityActive = location.pathname === '/community' || communityItems.some((i) => isActive(i.path));

  return (
    <nav className="hidden md:flex items-center space-x-1" aria-label="Main navigation">
      <div
        className="relative"
        onMouseEnter={() => setCommunityOpen(true)}
        onMouseLeave={() => setCommunityOpen(false)}
      >
        <Link
          to="/community"
          aria-haspopup="true"
          aria-expanded={communityOpen}
          className={`px-2 py-2 text-sm font-medium transition-colors rounded-xl inline-flex items-center ${
            isCommunityActive
              ? 'text-primary-600 dark:text-primary-400 font-semibold'
              : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Community <span className="ml-0.5">▾</span>
        </Link>
        {communityOpen && (
          <div
            className="absolute top-full left-0 mt-0.5 py-1 w-40 bg-white rounded-xl shadow-lg border border-gray-100 z-50"
            role="menu"
          >
            {communityItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg mx-1"
                role="menuitem"
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>

      <div
        className="relative"
        onMouseEnter={() => setExploreOpen(true)}
        onMouseLeave={() => setExploreOpen(false)}
      >
        <Link
          to="/explore"
          aria-haspopup="true"
          aria-expanded={exploreOpen}
          className={`px-2 py-2 text-sm font-medium transition-colors rounded-xl inline-flex items-center ${
            isExploreActive
              ? 'text-primary-600 dark:text-primary-400 font-semibold'
              : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Explore <span className="ml-0.5">▾</span>
        </Link>
        {exploreOpen && (
          <div
            className="absolute top-full left-0 mt-0.5 py-1 w-44 bg-white rounded-xl shadow-lg border border-gray-100 z-50"
            role="menu"
          >
            {exploreItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg mx-1"
                role="menuitem"
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>

      <Link to="/islam" className={getLinkClasses('/islam')}>
        Islam
      </Link>

      <Link to="/contact" className={getLinkClasses('/contact')}>
        Contact
      </Link>

      {user && (user.is_staff || user.is_superuser) && (
        <Link to="/scrapper" className={getLinkClasses('/scrapper')}>
          Scrapper
        </Link>
      )}

      {user && (user.is_staff || user.is_superuser || ['admin', 'moderator'].includes(user.role)) && (
        <Link to="/admin-portal" className={getLinkClasses('/admin-portal')}>
          Admin
        </Link>
      )}
    </nav>
  );
};

export default Navigation;
